/**
 * The search form as a whole: shared location and dates, plus one cargo
 * section per ticked mode, all in one antd form. This module turns its values
 * into the neutral multi-mode request and back. Each mode needs its own kind
 * of place (airport, port, address), so each ticked mode has its own origin and
 * destination.
 */
import dayjs, { Dayjs } from 'dayjs';

import type { LaneMode, LaneSearchRequest } from '../../../apis/lanePlanner';
import {
  PlaceValue,
  placeValueFromRequest,
  requestPlaceOf,
  truckingProblem,
} from './placeValue';
import {
  AirCargoValues,
  airCargoValuesFromRequest,
  buildAirCargo,
  defaultAirCargoValues,
} from './airRequest';
import {
  buildOceanCargo,
  defaultOceanCargoValues,
  OceanCargoValues,
  oceanCargoValuesFromRequest,
} from './oceanRequest';
import {
  buildLtlCargo,
  buildRoadCargo,
  defaultRoadCargoValues,
  RoadCargoValues,
  roadCargoValuesFromRequest,
} from './roadRequest';
import {
  airDropoffPickerDay,
  earliestAirDropoffDay,
  earliestOceanDropoffDate,
  resolveOceanDropoffDate,
} from './shipDates';

import type { SearchCandidates } from './siteSelection';

export { FLEXIBILITY_OPTIONS } from './flexibility';

export interface LocationValues {
  origin?: PlaceValue;
  destination?: PlaceValue;
}

export interface SharedValues {
  dropoffDate?: Dayjs;
  /** Days either side of the ship date; 0 is the exact date. */
  flexibilityDays?: number;
}

/** Every field of the form; only the ticked modes' sections are filled in. */
export type LaneFormValues = LocationValues &
  SharedValues &
  Partial<AirCargoValues> &
  Partial<OceanCargoValues> &
  Partial<RoadCargoValues> & {
    /** Zoomlogi-only packaging catalog selection; not sent to vendors. */
    packagingOption?: string;
    /** Door to door mode: trucking between each end's address and the airport or port (air and ocean). */
    doorToDoor?: boolean;
  };

export interface SearchMeta {
  packagingOption?: string;
  reference?: string;
  /** The boxes as searched: the candidate airports and ports the results filters offer. */
  candidates?: SearchCandidates;
}

/** The earliest ship day every ticked mode's vendors accept. */
export const earliestShipDay = (modes: LaneMode[]): Dayjs => {
  const days: Dayjs[] = [dayjs().startOf('day')];
  if (modes.includes('air')) days.push(earliestAirDropoffDay());
  if (modes.includes('ocean')) days.push(dayjs(earliestOceanDropoffDate()));
  return days.reduce((latest, day) => (day.isAfter(latest) ? day : latest));
};

/**
 * The ticked modes once the company's quotable modes are known: unchanged
 * while any ticked mode can be searched (a filled-in form is never trimmed),
 * otherwise the first quotable mode.
 */
export const modesAfterConfig = (
  current: LaneMode[],
  quotable: LaneMode[]
): LaneMode[] =>
  quotable.length === 0 || current.some((mode) => quotable.includes(mode))
    ? current
    : [quotable[0]];

/** Built at mount so the ship date is not frozen at module load. */
export const defaultLaneFormValues = (
  modes: LaneMode[] = ['air']
): LaneFormValues => ({
  ...defaultAirCargoValues(),
  ...defaultOceanCargoValues(),
  ...defaultRoadCargoValues(),
  dropoffDate: earliestShipDay(modes),
  flexibilityDays: 0,
  doorToDoor: false,
});

/** The request without pickup and delivery trucking (for a company that cannot quote it). */
export const withoutTrucking = (
  request: LaneSearchRequest
): LaneSearchRequest => {
  const { door_to_door: _trucking, ...rest } = request;
  return rest;
};

const NO_PLACE: PlaceValue = { label: '', airports: [], ports: [] };

/** Pickup and delivery trucking goes with an air or ocean search; a Road tile beside it stays door to door by itself. */
export const canTruck = (modes: LaneMode[]): boolean =>
  modes.includes('air') || modes.includes('ocean');

/** With Road ticked, trucking reuses the Road shipment, which has to be LTL. */
export const roadTruckingProblem = (
  modes: LaneMode[],
  roadType: RoadCargoValues['roadType'] | undefined
): string | null =>
  modes.includes('road') && (roadType ?? 'ltl') !== 'ltl'
    ? 'Trucking is priced as LTL: set the Road type to LTL.'
    : null;

/**
 * The trucking the form asks for: each end that can be trucked (an end that
 * cannot, for example an airport picked by name, is left out); null when the
 * switch is off or no end remains.
 */
export const doorToDoorEnds = (
  values: LaneFormValues,
  modes: LaneMode[]
): { pickup: boolean; delivery: boolean } | null => {
  if (
    !values.doorToDoor ||
    !canTruck(modes) ||
    roadTruckingProblem(modes, values.roadType)
  ) {
    return null;
  }
  const pickup = truckingProblem(values.origin, modes) === null;
  const delivery = truckingProblem(values.destination, modes) === null;
  return pickup || delivery ? { pickup, delivery } : null;
};

/** Whether the form asks for a trucking cargo: some end is trucked and Road's own shipment is not the cargo. */
export const needsTruckingCargo = (
  values: LaneFormValues,
  modes: LaneMode[]
): boolean => !modes.includes('road') && doorToDoorEnds(values, modes) !== null;

/**
 * Validated form values -> the request sent to `POST searches/`, for the ticked
 * modes. `trucking` is whether the company can quote pickup and delivery trucking
 * (a re-run saved search may carry a tick it can no longer honour).
 */
export const buildLaneRequest = (
  values: LaneFormValues,
  modes: LaneMode[],
  trucking = true
): LaneSearchRequest => {
  const shipDay = values.dropoffDate ?? earliestShipDay(modes);
  const door = trucking ? doorToDoorEnds(values, modes) : null;
  const origin = requestPlaceOf(values.origin ?? NO_PLACE, modes, door?.pickup);
  const destination = requestPlaceOf(
    values.destination ?? NO_PLACE,
    modes,
    door?.delivery
  );
  const request: LaneSearchRequest = {
    origin: origin.place,
    destination: destination.place,
    ...(Object.keys(origin.sites).length ? { origin_sites: origin.sites } : {}),
    ...(Object.keys(destination.sites).length
      ? { destination_sites: destination.sites }
      : {}),
    ship_date: shipDay.format('YYYY-MM-DD'),
    ...(values.flexibilityDays
      ? { flexibility_days: values.flexibilityDays }
      : {}),
    modes,
  };
  if (modes.includes('air')) {
    request.air = buildAirCargo(values as AirCargoValues);
  }
  if (modes.includes('ocean')) {
    request.ocean = buildOceanCargo(values as OceanCargoValues);
  }
  if (modes.includes('road')) {
    request.road = buildRoadCargo(values as RoadCargoValues);
  }
  if (door) {
    // always dry-van LTL; with Road ticked, the Road shipment's own cargo
    const road = buildLtlCargo({
      ...(values as RoadCargoValues),
      roadType: 'ltl',
      equipment: 'Dry van',
    });
    request.door_to_door = { ...door, road };
  }
  return request;
};

export const searchMetaOf = (
  values: LaneFormValues,
  modes: LaneMode[]
): SearchMeta => ({
  packagingOption: modes.includes('air')
    ? values.packagingOption || undefined
    : undefined,
  reference: modes.includes('ocean')
    ? values.reference?.trim() || undefined
    : undefined,
  candidates:
    values.origin && values.destination
      ? { origin: values.origin, destination: values.destination }
      : undefined,
});

/** Ship date as the picker shows it: never before the earliest accepted day. */
export const clampedShipDay = (shipDate: string, modes: LaneMode[]): Dayjs => {
  let day = dayjs(shipDate);
  if (modes.includes('air')) {
    const air = airDropoffPickerDay(shipDate);
    if (air.isAfter(day)) day = air;
  }
  if (modes.includes('ocean')) {
    const ocean = dayjs(resolveOceanDropoffDate(shipDate));
    if (ocean.isAfter(day)) day = ocean;
  }
  const today = dayjs().startOf('day');
  return day.isBefore(today) ? today : day;
};

/** Reverse of `buildLaneRequest`: a stored request -> form values. */
export const laneFormValuesFromRequest = (
  request: LaneSearchRequest
): LaneFormValues => ({
  ...defaultLaneFormValues(request.modes),
  origin: placeValueFromRequest(request.origin, request.origin_sites),
  destination: placeValueFromRequest(
    request.destination,
    request.destination_sites
  ),
  dropoffDate: clampedShipDay(request.ship_date, request.modes),
  flexibilityDays: request.flexibility_days ?? 0,
  ...(request.air ? airCargoValuesFromRequest(request.air) : {}),
  ...(request.ocean ? oceanCargoValuesFromRequest(request.ocean) : {}),
  ...(request.door_to_door
    ? {
        doorToDoor:
          request.door_to_door.pickup || request.door_to_door.delivery,
        ...roadCargoValuesFromRequest(request.door_to_door.road),
      }
    : {}),
  // after the trucking cargo, which shares its fields: the Road shipment wins
  ...(request.road ? roadCargoValuesFromRequest(request.road) : {}),
});
